import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/* ------------------------------------------------------------------
   Pohjautuu 32_Call_of_Zulun src/render/pipeline.js:ään, supistettuna:
   EI GTAO:ta (raskas, ei oletuksena), EI custom grade-shaderia (pelin
   oma aihe päättää värimaailman) — RenderPass -> Bloom -> SMAA ->
   OutputPass, ACES-tone mapping. Molemmat on helppo lisätä myöhemmin
   per peli samaan kaavaan (ks. Call_of_Zulun versio referenssinä).

   Jokainen valinnainen passi on try/catch:ssa: ajurin puute ei saa
   kaataa koko framea, vain sen efektin.
------------------------------------------------------------------ */

export const QUALITY = {
  high: { scale: 1.0, bloom: true, smaa: true },
  medium: { scale: 0.85, bloom: true, smaa: true },
  low: { scale: 0.7, bloom: false, smaa: false },
};

export function createRenderer(canvas) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // SMAA hoitaa tämän
    powerPreference: 'high-performance',
    stencil: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  return renderer;
}

export class Pipeline {
  constructor(renderer, scene, camera, quality = 'high') {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.quality = quality;
    this.warnings = [];
    this._build();
  }

  _build() {
    const q = QUALITY[this.quality];
    const { w, h } = this._size();

    const target = new THREE.WebGLRenderTarget(w, h, {
      type: THREE.HalfFloatType,
      colorSpace: THREE.LinearSRGBColorSpace,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.setSize(w, h);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.bloom = null;
    if (q.bloom) {
      try {
        this.bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.45, 0.4, 0.8);
        this.composer.addPass(this.bloom);
      } catch (err) {
        this.warnings.push(`Bloom ei käytettävissä: ${err.message}`);
      }
    }

    if (q.smaa) {
      try {
        const smaa = new SMAAPass();
        smaa.setSize?.(w, h);
        this.composer.addPass(smaa);
      } catch (err) {
        this.warnings.push(`SMAA ei käytettävissä: ${err.message}`);
      }
    }

    this.composer.addPass(new OutputPass());
  }

  _size() {
    const q = QUALITY[this.quality];
    const dpr = this.renderer.getPixelRatio();
    return {
      w: Math.max(320, Math.floor(window.innerWidth * dpr * q.scale)),
      h: Math.max(240, Math.floor(window.innerHeight * dpr * q.scale)),
    };
  }

  setQuality(name) {
    if (!QUALITY[name] || name === this.quality) return;
    this.quality = name;
    this.composer.passes.forEach((p) => p.dispose?.());
    this.composer.renderTarget1?.dispose();
    this.composer.renderTarget2?.dispose();
    this._build();
    this.resize();
  }

  resize() {
    const { w, h } = this._size();
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.composer.setSize(w, h);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  render(dt) {
    this.composer.render(dt);
  }
}
